import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { submitCommand } from '../../sync/core/submitCommand';
// Leaf import (no sync/infra-api): evita cycle + RN en tests de appendPromotionEvent.

export type PromotionEventType =
  | 'create'
  | 'update'
  | 'activate'
  | 'deactivate'
  | 'delete'
  | 'restore';

export type PromotionEventRow = {
  id: string;
  promotionId: string;
  eventType: PromotionEventType;
  summary: string;
  payload: Record<string, unknown>;
  createdAt: string;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
};

export type PromotionHistoryFilters = {
  fromCreatedAt?: string;
  toCreatedAt?: string;
  eventType?: PromotionEventType | 'all';
};

const parsePayloadJson = (raw: unknown): Record<string, unknown> => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string') return {};
  try {
    const v = JSON.parse(raw) as unknown;
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

type PromotionEventDbRow = {
  id: string;
  promotion_id: string;
  event_type: string;
  summary: string;
  payload_json: unknown;
  created_at: string;
  performed_by_user_id: string | null;
  origin_device_id: string | null;
};

const mapPromotionEventRow = (row: PromotionEventDbRow): PromotionEventRow => ({
  id: row.id,
  promotionId: row.promotion_id,
  eventType: row.event_type as PromotionEventType,
  summary: row.summary,
  payload: parsePayloadJson(row.payload_json),
  createdAt: row.created_at,
  performedByUserId: row.performed_by_user_id ?? undefined,
  originDeviceId: row.origin_device_id ?? undefined,
});

export const getPromotionHistory = async (
  promotionId: string,
  limit = 20,
  offset = 0,
  filters?: PromotionHistoryFilters
): Promise<PromotionEventRow[]> => {
  let query = supabase.schema('pos')
    .from('promotion_events')
    .select('id, promotion_id, event_type, summary, payload_json, created_at, performed_by_user_id, origin_device_id')
    .eq('promotion_id', promotionId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (filters?.fromCreatedAt) {
    query = query.gte('created_at', filters.fromCreatedAt);
  }
  if (filters?.toCreatedAt) {
    query = query.lte('created_at', filters.toCreatedAt);
  }
  if (filters?.eventType && filters.eventType !== 'all') {
    query = query.eq('event_type', filters.eventType);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map(mapPromotionEventRow);
};

export const getPromotionHistoryCount = async (
  promotionId: string,
  filters?: PromotionHistoryFilters
): Promise<number> => {
  let query = supabase.schema('pos')
    .from('promotion_events')
    .select('*', { count: 'exact', head: true })
    .eq('promotion_id', promotionId);

  if (filters?.fromCreatedAt) {
    query = query.gte('created_at', filters.fromCreatedAt);
  }
  if (filters?.toCreatedAt) {
    query = query.lte('created_at', filters.toCreatedAt);
  }
  if (filters?.eventType && filters.eventType !== 'all') {
    query = query.eq('event_type', filters.eventType);
  }

  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
};

/**
 * Persiste un evento de historial vía `pos.apply_promotion_event_command`.
 * Post-effect best-effort (§4.1): si falla, se observa en Sentry y no tira
 * la mutación de promo ya confirmada.
 */
export async function appendPromotionEvent(params: {
  promotionId: string;
  eventType: PromotionEventType;
  summary: string;
  payload?: Record<string, unknown>;
  performedByUserId?: string | null;
}): Promise<void> {
  const id = crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const performedByUserId =
    params.performedByUserId ?? useAuthStore.getState().session?.user?.id ?? null;

  try {
    const result = await submitCommand({
      entity: 'promotion_event',
      operation: 'insert',
      payload: {
        id,
        operationId: id,
        promotionId: params.promotionId,
        eventType: params.eventType,
        summary: params.summary,
        payloadJson: params.payload ?? {},
        createdAt,
        performedByUserId,
      },
    });
    if (!result.applied) {
      throw new Error(result.ingestError?.trim() || 'promotion_event_not_applied');
    }
  } catch (error: unknown) {
    const err = error instanceof Error ? error : new Error(String(error));
    const offline =
      err.name === 'OfflineWriteBlockedError' ||
      (typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        (error as { code?: string }).code === 'offline_write_blocked');
    captureOpsFailure({
      module: 'promotions',
      op: offline ? 'promotions.event_offline' : 'promotions.event_write',
      error: err,
      ingestError: err.message,
      applied: false,
      itemId: params.promotionId,
      extra: { eventType: params.eventType, eventId: id },
    });
  }
}

export function getPromotionEventTypeLabel(eventType: PromotionEventType | string): string {
  const map: Record<string, string> = {
    create: 'Creada',
    update: 'Editada',
    activate: 'Activada',
    deactivate: 'Pausada',
    delete: 'Borrada',
    restore: 'Restaurada',
  };
  return map[eventType] ?? String(eventType);
}
